import Menu from './menu';

import type { BookData, BookEntry } from '@kaetram/common/types/messages/outgoing';

const ITEMS_PER_PAGE = 8;

export default class BookAdmin extends Menu {
    private categoryList: HTMLElement = document.querySelector('#book-category-list')!;
    private pageTitle: HTMLElement = document.querySelector('#book-page-title')!;
    private pageContent: HTMLElement = document.querySelector('#book-page-content')!;
    private pageInfo: HTMLElement = document.querySelector('#book-page-info')!;
    private prevBtn: HTMLElement = document.querySelector('#book-prev')!;
    private nextBtn: HTMLElement = document.querySelector('#book-next')!;

    private data: BookData | null = null;
    private categories: string[] = [];
    private currentCategory = '';
    private currentPage = 0;

    public constructor() {
        super('#book-admin', '#book-admin-close');

        this.prevBtn.addEventListener('click', () => this.turnPage(-1));
        this.nextBtn.addEventListener('click', () => this.turnPage(1));
    }

    public override show(bookData: BookData): void {
        this.data = bookData;
        this.categories = Object.keys(bookData.categories);

        this.renderCategories();

        if (this.categories.length > 0) this.selectCategory(this.categories[0]);

        this.container.classList.add('visible');
    }

    public override hide(): void {
        this.container.classList.remove('visible');
    }

    private renderCategories(): void {
        this.categoryList.innerHTML = '';

        for (let cat of this.categories) {
            let btn = document.createElement('div');
            btn.className = 'book-cat-btn';
            btn.textContent = cat;
            btn.addEventListener('click', () => this.selectCategory(cat));
            this.categoryList.appendChild(btn);
        }
    }

    private selectCategory(category: string): void {
        this.currentCategory = category;
        this.currentPage = 0;

        for (let btn of this.categoryList.children)
            btn.classList.toggle('active', btn.textContent === category);

        this.renderPage();
    }

    private renderPage(): void {
        if (!this.data) return;

        let entries = this.data.categories[this.currentCategory] || [];
        let totalPages = Math.max(1, Math.ceil(entries.length / ITEMS_PER_PAGE));
        let start = this.currentPage * ITEMS_PER_PAGE;
        let pageEntries = entries.slice(start, start + ITEMS_PER_PAGE);

        this.pageTitle.textContent = this.currentCategory;
        this.pageInfo.textContent = `${this.currentPage + 1} / ${totalPages}`;

        this.prevBtn.classList.toggle('disabled', this.currentPage <= 0);
        this.nextBtn.classList.toggle('disabled', this.currentPage >= totalPages - 1);

        this.pageContent.innerHTML = '';

        for (let entry of pageEntries) {
            let row = document.createElement('div');
            row.className = 'book-entry';
            row.innerHTML = this.renderEntry(entry);
            this.pageContent.appendChild(row);
        }
    }

    private renderEntry(entry: BookEntry): string {
        let html = `<div class="book-entry-header">`;
        html += `<span class="book-entry-name">${entry.name}</span>`;
        if (entry.level) html += `<span class="book-entry-level">Nv. ${entry.level}</span>`;
        html += `</div>`;

        html += `<div class="book-entry-key">${entry.key}</div>`;

        if (entry.description)
            html += `<div class="book-entry-desc">${entry.description}</div>`;

        let statsHtml = '';

        if (entry.hitPoints)
            statsHtml += `<span class="book-stat"><b>HP:</b> ${entry.hitPoints}</span>`;

        if (entry.price)
            statsHtml += `<span class="book-stat"><b>Precio:</b> ${entry.price}g</span>`;

        if (entry.aggressive !== undefined)
            statsHtml += `<span class="book-stat"><b>Agresivo:</b> ${entry.aggressive ? 'Si' : 'No'}</span>`;

        if (statsHtml)
            html += `<div class="book-entry-stats">${statsHtml}</div>`;

        if (entry.attackStats) {
            let atk = Object.entries(entry.attackStats).filter(([, v]) => v > 0)
                .map(([k, v]) => `${k}: ${v}`).join(', ');
            if (atk) html += `<div class="book-entry-stats"><b>Ataque:</b> ${atk}</div>`;
        }

        if (entry.defenseStats) {
            let def = Object.entries(entry.defenseStats).filter(([, v]) => v > 0)
                .map(([k, v]) => `${k}: ${v}`).join(', ');
            if (def) html += `<div class="book-entry-stats"><b>Defensa:</b> ${def}</div>`;
        }

        if (entry.bonuses) {
            let bon = Object.entries(entry.bonuses).filter(([, v]) => v > 0)
                .map(([k, v]) => `${k}: ${v}`).join(', ');
            if (bon) html += `<div class="book-entry-stats"><b>Bonuses:</b> ${bon}</div>`;
        }

        if (entry.drops) {
            let dr = Object.entries(entry.drops).map(([k, v]) => `${k} (${v / 10}%)`).join(', ');
            html += `<div class="book-entry-stats"><b>Drops:</b> ${dr}</div>`;
        }

        return html;
    }

    private turnPage(delta: number): void {
        if (!this.data) return;

        let entries = this.data.categories[this.currentCategory] || [];
        let totalPages = Math.max(1, Math.ceil(entries.length / ITEMS_PER_PAGE));
        let newPage = this.currentPage + delta;

        if (newPage < 0 || newPage >= totalPages) return;

        this.currentPage = newPage;
        this.renderPage();
    }
}
